import DocIcon from "@mui/icons-material/TextSnippet";
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPackageDoc } from "../admin/packages/packagesApi";
import type { UserRole } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import DownloadFileButton from "../components/downloadFileButton/DownloadFileButton";
import ImagePreview from "../components/imagePreview/ImagePreview";
import ImagesGallery from "../components/imagesGallery/ImagesGallery";
import { TripTagCategory } from "../components/tagSelect/tags";
import { useDownloadImages } from "../misc/useDownloadImages";
import PackageTimeline from "./PackageTimeline";
import PublicPackageCustomers from "./PublicPackageCustomers";
import type { PublicPackageResponse } from "./publicPackagesApi";
import SimilarPackages from "./SimilarPackages";
import { usePublicPackage } from "./usePublicPackage";

function getAllowedDayDetailTypes(days: PublicPackageResponse["days"]) {
  const hasDescriptions = days.some((d) => d.details.description);
  const hasPlans = days.some((d) => d.details.plan);
  if (hasDescriptions && hasPlans) {
    return ["DESCRIPTION", "PLAN"] as const;
  }
  if (hasPlans) {
    return ["PLAN"] as const;
  }
  return ["DESCRIPTION"] as const;
}

interface TimelineHeaderProps {
  readonly dayDetailsType: "DESCRIPTION" | "PLAN";
  readonly onChange: (v: "DESCRIPTION" | "PLAN") => void;
  readonly days: PublicPackageResponse["days"];
}

const TimelineHeader: FC<TimelineHeaderProps> = ({ dayDetailsType, onChange, days }) => {
  const allowedDayDetailTypes = getAllowedDayDetailTypes(days);
  if (allowedDayDetailTypes.length > 1) {
    return (
      <Stack direction="row" spacing={2}>
        <Typography
          variant="h6"
          component={Link}
          color={dayDetailsType === "PLAN" ? "primary" : "inherit"}
          underline="none"
          onClick={() => onChange("PLAN")}
          sx={{ cursor: "pointer" }}
        >
          Plan wycieczki
        </Typography>
        <Typography variant="h6">/</Typography>
        <Typography
          variant="h6"
          component={Link}
          color={dayDetailsType === "DESCRIPTION" ? "primary" : "inherit"}
          underline="none"
          onClick={() => onChange("DESCRIPTION")}
          sx={{ cursor: "pointer" }}
        >
          Opis wycieczki
        </Typography>
      </Stack>
    );
  }
  return <Typography variant="h6">{dayDetailsType === "PLAN" ? "Plan wycieczki" : "Opis wycieczki"}</Typography>;
};

interface ActionProps {
  readonly id: string;
  readonly name: string;
  readonly images: { name: string; source?: string; info?: string; original: { url: string } }[];
  readonly role: UserRole | null;
  readonly dayDetailsType: "DESCRIPTION" | "PLAN";
}

const Actions: FC<ActionProps> = ({ id, name, images, role, dayDetailsType }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { download } = useDownloadImages();
  const packageUserFriendlyId = name.substring(0, 3);

  const isCustomer = role === "CUSTOMER";
  return (
    <Stack direction="row" spacing={2}>
      <AsyncLoadingButton
        color="secondary"
        disabled={images.length === 0}
        onClick={async () => {
          await download(images.map((i) => ({ name: i.name, url: i.original.url, info: i.info, source: i.source })));
        }}
      >
        Pobierz zdjęcia
      </AsyncLoadingButton>
      {isCustomer && (
        <DownloadFileButton
          buttonText="Pobierz program z własnym logo"
          startIcon={<DocIcon />}
          filename={`${name}.docx`}
          options={[
            {
              name: "Bez cen opcji",
              apiDefinition: async () => {
                const blob = await getPackageDoc(id, dayDetailsType);
                if (!blob) {
                  throw new Error("Brak dokumentu pakietu");
                }
                return blob;
              },
            },
          ]}
          onError={() => enqueueSnackbar("Nie udało się pobrać pliku z programem", { variant: "error" })}
        />
      )}
      <Button variant="outlined" color="secondary" onClick={() => navigate(`/calculations/new?fromPackage=${id}`)}>
        Wyceń pakiet
      </Button>
      <Button variant="outlined" color="primary" onClick={() => navigate(`/e-commerce?query=${packageUserFriendlyId}`)}>
        Sprawdź w e-commerce
      </Button>
    </Stack>
  );
};

interface PackageViewProps {
  readonly value: PublicPackageResponse;
  readonly maxWidth: number;
  readonly role: UserRole | null;
}

const PackageView: FC<PackageViewProps> = ({ value, maxWidth, role }) => {
  const [dayDetailsType, setDayDetailsType] = useState<"DESCRIPTION" | "PLAN">(
    () => getAllowedDayDetailTypes(value.days)[0],
  );
  const groupTypeTags = value.tags.filter((t) => t.category === TripTagCategory.GROUP_TYPE);
  return (
    <Grid container size={12} spacing={2}>
      <Grid size={12}>
        <ImagesGallery images={value.media.images.map((i) => ({ name: i.name, url: i.www.url }))} maxWidth={maxWidth} />
      </Grid>
      <Grid size={12}>
        <Typography variant="h4">{value.name}</Typography>
      </Grid>
      <Grid size={12}>
        <Typography>{value.packageName}</Typography>
      </Grid>
      <Grid size={12}>
        <Typography variant="h6">Co wyróżnia tę wycieczkę</Typography>
      </Grid>
      <Grid size={12}>
        <Typography>{value.description}</Typography>
      </Grid>
      <Grid size={6}>
        <Typography variant="h6">Trasa wycieczki</Typography>
        <List data-testid="package-route">
          {value.days.map((d) => (
            <ListItem key={d.dayNumber}>
              <ListItemText>{d.title}</ListItemText>
            </ListItem>
          ))}
        </List>
      </Grid>
      <Grid size={6}>
        <Box sx={{ float: "right" }}>
          <ImagePreview image={value.media.map} thumbnailSize="auto" />
        </Box>
      </Grid>
      <Grid size={12}>
        <TimelineHeader days={value.days} dayDetailsType={dayDetailsType} onChange={setDayDetailsType} />
      </Grid>
      <Grid size={12}>
        <PackageTimeline days={value.days} dayDetailsType={dayDetailsType} />
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Actions
          id={value.id.id}
          name={value.packageName}
          images={value.media.images}
          role={role}
          dayDetailsType={dayDetailsType}
        />
      </Grid>
      {value.customers.length > 0 && <PublicPackageCustomers customers={value.customers} />}
      {groupTypeTags.length > 0 && (
        <Grid
          size={12}
          container
          spacing={2}
          sx={{
            alignItems: "stretch",
          }}
        >
          <SimilarPackages currentPackageId={value.id.id} groupTypeTags={groupTypeTags} maxItems={3} />
        </Grid>
      )}
    </Grid>
  );
};

const PublicPackagePage: FC<{ role: UserRole | null }> = ({ role }) => {
  const { data, isLoading } = usePublicPackage();
  const maxWidth = 900;
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      {isLoading && <LinearProgress />}
      <Box
        sx={{
          maxWidth: `${maxWidth}px`,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {data && <PackageView value={data} maxWidth={maxWidth} role={role} />}
      </Box>
    </Grid>
  );
};

export default PublicPackagePage;
